import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Back-in-stock "Notify me" controller for snippets/oj-back-in-stock.liquid.
 *
 * Shows the panel only for a sold-out selected variant, keeps the variant
 * context (id/title/subject/body) in sync on variant change, and AJAX-submits
 * the Shopify contact form with loading/success/error states. No money or dates
 * are rendered here. Progressive enhancement: with JS off it is a normal
 * full-page /contact POST and Shopify re-renders the success state on return.
 */
class OjBackInStock extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  #ref(name) {
    return /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (this.querySelector(`[ref="${name}"]`));
  }

  #onVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const detail = event.detail || {};
    const productId = detail.data && detail.data.productId;
    // Combined-listing safe: ignore other products' variant changes.
    if (productId && String(productId) !== String(this.productId)) return;
    const variant = detail.resource;
    // No matching variant for the chosen options reads as unavailable.
    const soldOut = !variant || variant.available === false;
    this.#setSoldOut(soldOut, variant);
  };

  #setSoldOut(soldOut, variant) {
    // The editor forces the panel visible; never fight design mode.
    if (!window.Shopify?.designMode) {
      this.toggleAttribute('hidden', !soldOut);
    }
    if (variant) this.#syncVariant(variant);
    // A fresh selection is a fresh request — drop any prior success state.
    if (this.dataset.state === 'done') {
      delete this.dataset.state;
      const feedback = this.querySelector('.oj-bis__feedback');
      if (feedback) feedback.textContent = '';
    }
  }

  #syncVariant(variant) {
    const id = variant.id != null ? String(variant.id) : '';
    const title =
      variant.title || variant.public_title || variant.name || this.getAttribute('data-variant-title') || '';
    const product = this.getAttribute('data-product-title') || '';
    const url = this.getAttribute('data-product-url') || '';

    this.setAttribute('data-variant-id', id);
    if (title) this.setAttribute('data-variant-title', title);

    const variantIdField = this.#ref('variantIdField');
    if (variantIdField) variantIdField.value = id;
    const variantField = this.#ref('variantField');
    if (variantField) variantField.value = title;
    const subject = this.#ref('subject');
    if (subject) subject.value = `Back in stock request — ${product} (${title})`;
    const body = this.#ref('body');
    if (body) body.value = `Back in stock request.\nProduct: ${product}\nVariant: ${title}\nVariant ID: ${id}\nURL: ${url}`;
  }

  #onSubmit = async (/** @type {SubmitEvent} */ event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement) || typeof fetch !== 'function') return; // fall back to native POST
    event.preventDefault();

    const button = this.#ref('button');
    const feedback = this.querySelector('.oj-bis__feedback');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    if (feedback) {
      feedback.textContent = '';
      feedback.classList.remove('oj-bis__feedback--error');
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      // Shopify redirects to ?contact_posted=true on a successful send.
      const ok = response.ok && (response.url.includes('contact_posted=true') || response.redirected);
      if (ok) {
        this.dataset.state = 'done';
        if (feedback) feedback.textContent = this.getAttribute('data-success') || "We'll email you when it returns.";
      } else {
        let message = '';
        try {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          message = doc.querySelector('.oj-bis__feedback--error')?.textContent?.trim() || '';
        } catch (error) {
          /* ignore parse errors, fall through to generic message */
        }
        if (feedback) {
          feedback.textContent = message || 'Please enter a valid email address.';
          feedback.classList.add('oj-bis__feedback--error');
        }
      }
    } catch (error) {
      // Network failure — let the browser submit normally so nothing is lost.
      form.submit();
      return;
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  };
}

if (!customElements.get('oj-back-in-stock')) customElements.define('oj-back-in-stock', OjBackInStock);
